import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Tabs,
  Tab,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";
import { toast } from 'sonner';
import { getXeroPushErrorMessage, isXeroPushFailed } from '../../utils/xeroPushMessage';

const getTodayReportDefaults = () => {
  const today = new Date();
  const todayStr = format(today, 'dd/MM/yyyy');
  const reportrange = `${todayStr} - ${todayStr}`;
  return {
    dates: [today, today],
    reportrange,
    reportDate: `${todayStr} To Date : ${todayStr}`,
  };
};

const PushInvoiceReport = () => {
  const todayDefaults = getTodayReportDefaults();
  const [reportrange, setReportrange] = useState(todayDefaults.reportrange);
  const [dates, setDates] = useState(todayDefaults.dates);
  const [reportDate, setReportDate] = useState(todayDefaults.reportDate);
  const [salesOrderReport, setSalesOrderReport] = useState([]);
  const [totals, setTotals] = useState({
    netAmount: 0,
    taxTotal: 0,
    grandTotal: 0,
    paidTotal: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activePushTab, setActivePushTab] = useState('not_pushed');
  const [pushingOrderIds, setPushingOrderIds] = useState({});
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const currencySymbol = localStorage.getItem("currencySymbol") || '';
  const company_tax_type = localStorage.getItem("company_tax_type") || 'Tax';

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const startDate = format(value[0], 'dd/MM/yyyy');
      const endDate = format(value[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setReportDate(startDate + ' To Date : ' + endDate);
      setPage(0);
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('draw', 1);
      formData.append('start', page * rowsPerPage);
      formData.append('length', rowsPerPage);
      formData.append('searchValue', searchQuery);
      formData.append('reportrange', reportrange);
      formData.append('is_push', activePushTab === 'pushed' ? 'Y' : 'N');

      const response = await fetch(`${config.API_URL}/pushxeroinvoice`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to fetch report');
      const json = await response.json();

      if (json && json.data && Array.isArray(json.data)) {
        setSalesOrderReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        if (json.startdate && json.enddate) {
          setReportDate(json.startdate + ' To Date : ' + json.enddate);
        }
        setTotals({
          netAmount: parseFloat(json.sum_total_net || 0),
          taxTotal: parseFloat(json.sum_total_tax || 0),
          grandTotal: parseFloat(json.sum_grand_total || 0),
          paidTotal: parseFloat(json.sum_total_paid || 0),
        });
      } else {
        setSalesOrderReport([]);
        setTotalCount(0);
        setTotals({ netAmount: 0, taxTotal: 0, grandTotal: 0, paidTotal: 0 });
      }
    } catch (error) {
      setSalesOrderReport([]);
      setTotalCount(0);
      setTotals({ netAmount: 0, taxTotal: 0, grandTotal: 0, paidTotal: 0 });
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, searchQuery, activePushTab]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
  }, []);

  useEffect(() => {
    if (!reportrange) return;
    fetchReport();
  }, [reportrange, page, rowsPerPage, searchQuery, activePushTab, fetchReport]);

  const searchFormData = () => {
    if (dates[0] && dates[1]) {
      const startDate = format(dates[0], 'dd/MM/yyyy');
      const endDate = format(dates[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setReportDate(startDate + ' To Date : ' + endDate);
      setPage(0);
    }
  };

  const exportToExcel = async () => {
    const formData = new FormData();
    formData.append('draw', '1');
    formData.append('start', '0');
    formData.append("length", totalCount || 10000);
    formData.append("searchValue", searchQuery);
    formData.append("reportrange", reportrange);
    formData.append('is_push', activePushTab === 'pushed' ? 'Y' : 'N');

    try {
      const response = await fetch(`${config.API_URL}/ws-SalesOrder-report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      const exportData = (data.data || []).map(product => ({
        Date: product.date || '',
        "Customer (A/C)": product.company
          ? `${product.company} (${product.accound_no || ''})`
          : product.accound_no || '',
        "Order Number": product.reference_no || '',
        "Net Amount": parseFloat(product.net_amount || 0),
        [`${company_tax_type} Total`]: parseFloat(product.total_tax || 0),
        "Grand Total": parseFloat(product.grand_total || 0)
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Push Invoice Report');
      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const statusLabel = activePushTab === 'pushed' ? 'Pushed' : 'Not-Pushed';
      XLSX.writeFile(wb, `Push-Invoice-Report-${statusLabel}-${formattedDate}-${formattedTime}.xlsx`);
    } catch (error) {
    }
  };

  const pushXeroInvoiceByOrder = async (orderId) => {
    if (!orderId) {
      toast.error('Order id not found');
      return;
    }

    setPushingOrderIds((prev) => ({ ...prev, [orderId]: true }));
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('accountCode', '200');
      formData.append('status', 'AUTHORISED');
      formData.append('lineAmountTypes', 'Exclusive');

      const response = await fetch(`${config.API_URL}/ws-push-xero-invoice-by-order`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      let json = null;
      try {
        json = await response.json();
      } catch {
        json = null;
      }

      if (isXeroPushFailed(response, json)) {
        throw new Error(
          getXeroPushErrorMessage(json, 'Failed to push invoice')
        );
      }

      const successMsg = String(json?.message ?? '').trim();
      toast.success(
        successMsg &&
          successMsg.toLowerCase() !== 'false' &&
          successMsg.toLowerCase() !== 'true'
          ? successMsg
          : 'Invoice pushed'
      );
      await fetchReport();
    } catch (error) {
      toast.error(
        error?.message ||
          getXeroPushErrorMessage(null, 'Failed to push invoice')
      );
    } finally {
      setPushingOrderIds((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const formatNumber = (num) => parseFloat(num || 0).toFixed(2);

  return (
    <Box className="page-container">
      <Paper className="paper-style">
        <Box sx={{ px: 2, py: 1.2 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1, gap: 2 }}>
            <Typography className="page-title">Push Invoice Report</Typography>
            <Button
              variant="contained"
              className="button-variant-contained"
              href={`${config.API_URL}/api/xero/connect?tenant=${config.PREFIX || accessToken || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Iconify icon="simple-icons:xero" width={18} />}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Connect Xero
            </Button>
          </Box>
          <Tabs className="common-tabs" sx={{ my: 1 }} value={activePushTab} onChange={(event, newValue) => { setActivePushTab(newValue); setPage(0); }}>
            <Tab label="Not Pushed Invoice" value="not_pushed" />
            <Tab label="Pushed Invoice" value="pushed" />
          </Tabs>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <TextField
                className="input-border"
                fullWidth
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-outline" width={20} sx={{ color: "#637381" }} /></InputAdornment>,
                    endAdornment: searchQuery && <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearchQuery(''); setPage(0); }} sx={{ color: "#637381" }}><Iconify icon="eva:close-fill" width={20} /></IconButton></InputAdornment>,
                  },
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <CommonDatePicker className="input-border" value={dates} onChange={handleDateChange} format="dd/MM/yyyy" character=" - " />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 2 }} display="flex" alignItems="flex-end">
              <Stack direction="row" spacing={1} width="100%">
                <Button variant="contained" onClick={searchFormData} fullWidth className="button-variant-contained">Submit</Button>
                <Button variant="outlined" onClick={exportToExcel} fullWidth className="button-variant-outlined" startIcon={<Iconify icon="eva:download-outline" width={20} />}>Export</Button>
              </Stack>
            </Grid2>
          </Grid2>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={40} /></Box>
        ) : (
          <TableContainer>
            <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="60px">Sr.no</TableCell>
                  <TableCell width="100px">Date</TableCell>
                  <TableCell width="350px">Customer (A/C)</TableCell>
                  <TableCell width="150px">Order. Ref</TableCell>
                  <TableCell width="120px" align="right">Net. Amount</TableCell>
                  <TableCell width="120px" align="right">{company_tax_type}. Total</TableCell>
                  <TableCell width="120px" align="right">Gross. Total</TableCell>
                  <TableCell width="140px" align="center">
                    {activePushTab === 'pushed' ? 'Xero Push Date' : 'Push'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesOrderReport.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: "#212b36" }}>{reportrange ? "No data available" : "Please select a date range and click Submit"}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  salesOrderReport.map((row, index) => {
                    const orderId = row?.id;
                    const isPushing = !!pushingOrderIds?.[orderId];
                    return (
                    <TableRow key={index} className="table-cell-style" sx={{ "&:last-child td": { borderBottom: "none" } }}>
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{row.date || ""}</TableCell>
                      <TableCell>{row.company ? `${row.company} (${row.accound_no || ''})` : ""}</TableCell>
                      <TableCell>
                        <MuiLink href={`${config.BASE_URL}/reports/mis_report/print/${row.id}`} target="_blank" rel="noopener noreferrer" sx={{ color: "#212b36", textDecoration: "underline !important", "&:hover": { textDecoration: "underline !important" } }}>
                          {row.reference_no || ""}
                        </MuiLink>
                      </TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(row.net_amount)}</TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(row.total_tax)}</TableCell>
                      <TableCell sx={{ textAlign: "right" }}>{currencySymbol} {formatNumber(row.grand_total)}</TableCell>
                      <TableCell align="center">
                        {activePushTab === 'pushed' ? (
                          row.xero_push_date || "-"
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={isPushing}
                            onClick={() => pushXeroInvoiceByOrder(orderId)}
                            className="button-variant-contained"
                          >
                            {isPushing ? 'Pushing...' : 'Push'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ "& .MuiTableCell-root": { fontSize: "0.75rem", fontWeight: 600, color: "#212b36", py: 1.2, textAlign: "right", backgroundColor: "#f5f7fa", borderBottom: "none" } }}>
                  <TableCell colSpan={4} sx={{ borderBottom: "none" }} />
                  <TableCell>{currencySymbol} {formatNumber(totals.netAmount)}</TableCell>
                  <TableCell>{currencySymbol} {formatNumber(totals.taxTotal)}</TableCell>
                  <TableCell>{currencySymbol} {formatNumber(totals.grandTotal)}</TableCell>
                  <TableCell sx={{ borderBottom: "none" }} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
        {!loading && salesOrderReport.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
              className="pagination-styles"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PushInvoiceReport;
